import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const chartColors = {
  teal: { bg: 'rgba(0, 201, 184, 0.15)', border: '#00c9b8' },
  blue: { bg: 'rgba(59, 130, 246, 0.15)', border: '#3b82f6' },
  purple: { bg: 'rgba(139, 92, 246, 0.15)', border: '#8b5cf6' },
  orange: { bg: 'rgba(249, 115, 22, 0.15)', border: '#f97316' },
  red: { bg: 'rgba(239, 68, 68, 0.15)', border: '#ef4444' },
  green: { bg: 'rgba(34, 197, 94, 0.15)', border: '#22c55e' },
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
};

export const GrowthChart = ({ data, title }) => {
  if (!data?.users?.length) return null;

  const labels = data.users.map(d => formatDate(d.date));

  // Cumulative sums
  let userSum = 0;
  const userCumulative = data.users.map(d => { userSum += d.count; return userSum; });
  let listingSum = 0;
  const listingCumulative = data.listings.map(d => { listingSum += d.count; return listingSum; });

  const chartData = {
    labels,
    datasets: [
      {
        label: 'New Users',
        data: userCumulative,
        borderColor: chartColors.teal.border,
        backgroundColor: chartColors.teal.bg,
        fill: true,
        tension: 0.3,
        pointRadius: 1,
        pointHoverRadius: 4,
      },
      {
        label: 'New Listings',
        data: listingCumulative,
        borderColor: chartColors.blue.border,
        backgroundColor: chartColors.blue.bg,
        fill: true,
        tension: 0.3,
        pointRadius: 1,
        pointHoverRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, padding: 16 } },
      title: { display: !!title, text: title, font: { size: 14, weight: '600' } },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 10, font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1, font: { size: 11 } },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
    },
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="growth-chart">
      <h3 className="font-semibold text-gray-900 mb-4 text-sm">{title || 'Growth (Last 30 Days)'}</h3>
      <div style={{ height: 260 }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export const DailyActivityChart = ({ data }) => {
  if (!data?.users?.length) return null;

  const labels = data.users.map(d => formatDate(d.date));

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Users Signed Up',
        data: data.users.map(d => d.count),
        backgroundColor: chartColors.teal.border,
        borderRadius: 4,
      },
      {
        label: 'Listings Created',
        data: data.listings.map(d => d.count),
        backgroundColor: chartColors.blue.border,
        borderRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, padding: 16 } },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 10, font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1, font: { size: 11 } },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
    },
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="daily-activity-chart">
      <h3 className="font-semibold text-gray-900 mb-4 text-sm">Daily Activity (Last 30 Days)</h3>
      <div style={{ height: 260 }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export const SalesChart = ({ data }) => {
  if (!data?.sales?.length) return null;

  const labels = data.sales.map(d => formatDate(d.date));

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Revenue ($)',
        data: data.sales.map(d => d.revenue),
        borderColor: chartColors.green.border,
        backgroundColor: chartColors.green.bg,
        fill: true,
        tension: 0.3,
        pointRadius: 1,
        pointHoverRadius: 4,
        yAxisID: 'y',
      },
      {
        label: 'Orders',
        data: data.sales.map(d => d.count),
        borderColor: chartColors.purple.border,
        backgroundColor: chartColors.purple.bg,
        fill: false,
        tension: 0.3,
        pointRadius: 1,
        pointHoverRadius: 4,
        yAxisID: 'y1',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, padding: 16 } },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 10, font: { size: 11 } },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        title: { display: true, text: 'Revenue ($)', font: { size: 11 } },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        title: { display: true, text: 'Orders', font: { size: 11 } },
        grid: { drawOnChartArea: false },
        ticks: { stepSize: 1 },
      },
    },
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="sales-chart">
      <h3 className="font-semibold text-gray-900 mb-4 text-sm">Sales & Revenue (Last 30 Days)</h3>
      <div style={{ height: 260 }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export const CategoryChart = ({ categories }) => {
  if (!categories?.length) return null;

  const top = categories.slice(0, 8);
  const colors = [
    '#00c9b8', '#3b82f6', '#8b5cf6', '#f97316',
    '#ef4444', '#22c55e', '#eab308', '#ec4899',
  ];

  const chartData = {
    labels: top.map(c => c.category || 'Unknown'),
    datasets: [{
      data: top.map(c => c.count),
      backgroundColor: colors.slice(0, top.length),
      borderWidth: 0,
      hoverOffset: 6,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: { usePointStyle: true, padding: 10, font: { size: 11 } },
      },
    },
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="category-chart">
      <h3 className="font-semibold text-gray-900 mb-4 text-sm">Listings by Category</h3>
      <div style={{ height: 240 }}>
        <Doughnut data={chartData} options={options} />
      </div>
    </div>
  );
};

export const UserActivityChart = ({ userActivity }) => {
  if (!userActivity) return null;

  const { active_today, active_this_week, inactive_users } = userActivity;
  const total = active_today + active_this_week + inactive_users;
  if (total === 0) return null;

  const chartData = {
    labels: ['Active Today', 'Active This Week', 'Inactive (7d+)'],
    datasets: [{
      data: [active_today, active_this_week, inactive_users],
      backgroundColor: ['#00c9b8', '#3b82f6', '#e5e7eb'],
      borderWidth: 0,
      hoverOffset: 6,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { usePointStyle: true, padding: 12, font: { size: 11 } },
      },
    },
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="user-activity-chart">
      <h3 className="font-semibold text-gray-900 mb-4 text-sm">User Engagement</h3>
      <div style={{ height: 240 }}>
        <Doughnut data={chartData} options={options} />
      </div>
    </div>
  );
};

export const TopProductsChart = ({ products }) => {
  if (!products?.length) return null;

  const top = products.filter(p => p.views > 0).slice(0, 6);
  if (top.length === 0) return null;

  const chartData = {
    labels: top.map(p => p.title.length > 25 ? p.title.slice(0, 25) + '...' : p.title),
    datasets: [{
      label: 'Views',
      data: top.map(p => p.views),
      backgroundColor: '#00c9b8',
      borderRadius: 4,
    }],
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { font: { size: 11 } },
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11 } },
      },
    },
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="top-products-chart">
      <h3 className="font-semibold text-gray-900 mb-4 text-sm">Most Viewed Products</h3>
      <div style={{ height: 240 }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};
